"use client";
import Image from "next/image";
import Link from "next/link";

export default function MsuAdvantages({ course }) {
  const studyData = course?.MsuAdvantages;
  const media = course?.MsuAdvantagesMedia;

  // Return null if no study data exists
  if (!studyData) {
    return null;
  }

  // Helper function to render rich text with bold support
  const renderRichText = (richTextArray) => {
    if (!richTextArray || richTextArray.length === 0) {
      return null;
    }

    return richTextArray.map((block, blockIndex) => {
      if (block.type === "list" && block.format === "unordered") {
        return (
          <ul key={blockIndex} className="space-y-[10px] border-1 border-[#07F3FF] p-4">
            {block.children?.map((listItem, itemIndex) => {
              if (listItem.type === "list-item" && listItem.children) {
                const content = listItem.children.map(
                  (fragment, fragmentIndex) => {
                    if (fragment.bold) {
                      return (
                        <strong key={fragmentIndex}>{fragment.text}</strong>
                      );
                    }
                    return <span key={fragmentIndex}>{fragment.text}</span>;
                  }
                );

                return (
                  <li
                    key={itemIndex}
                    className="flex items-start gap-[12px] text-base text-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <path
                        d="M10.0835 19.4915C9.99553 17.2599 9.06897 15.1435 7.48984 13.5643C5.9107 11.985 3.79369 11.0592 1.56307 10.971L0 10.5055L1.61652 10.0724C3.83822 9.99296 5.94856 9.08 7.5277 7.51512C9.10572 5.95024 10.0378 3.84836 10.1369 1.62755L10.6459 0L10.9922 1.4977C11.0624 3.74156 11.98 5.8754 13.5614 7.4689C15.1427 9.06229 17.2698 9.99653 19.5127 10.0832L21 10.5055L19.4481 10.9276C17.2152 11.0133 15.0982 11.9385 13.518 13.5182C11.9388 15.098 11.0134 17.2158 10.9276 19.4482L10.5056 21L10.0835 19.4915Z"
                        fill="#07F3FF"
                      />
                    </svg>
                    <span className="text-[#07F3FF]">{content}</span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        );
      }
      return null;
    });
  };

  return (
    <section className="py-[38px] md:py-[50px] bg-[#011F31]">
      <div className="xl:max-w-[var(--max-page-width)] xl:px-[var(--container-padding)] mx-auto">
        {/* Top Header Section */}
        <div className="px-[16px] md:px-[32px] xl:px-0 mb-[24px] md:mb-[32px]">
          <h2 className="text-[28px] md:text-[36px] lg:text-[42px] font-bold text-white mb-[12px] leading-tight">
            MSU Advantages
          </h2>
          <p className="text-[16px] md:text-[18px] text-white/80 max-w-[900px]">
            {studyData.Description}
          </p>
        </div>

        {/* Key Advantages - Cyan Highlights */}
        {studyData.AdvantagesList && (
          <div className="px-[16px] md:px-[32px] xl:px-0 mb-[32px] md:mb-[40px]">
            {renderRichText(studyData.AdvantagesList)}
          </div>
        )}

        {/* Main Content Section */}
        <div className="flex flex-col lg:flex-row px-[16px] md:px-[32px] xl:px-0 gap-0 mb-[24px] md:mb-[32px]">
          {/* Left Column: Highlights */}
          <div className="lg:w-1/2 w-full bg-white px-[24px] py-[32px] md:px-[38px] md:py-[42px] lg:px-[40px] lg:py-[50px]">
            <h3 className="text-[22px] md:text-[24px] font-bold text-black mb-[20px]">
              Highlights from The Programme:
            </h3>

            {studyData.Highlights && (
              <div className="space-y-[12px]">
                {studyData.Highlights[0]?.children
                  .map((listItem, index) => {
                    if (listItem.type === "list-item" && listItem.children) {
                      const content = listItem.children.map(
                        (fragment, fragIndex) => {
                          if (fragment.bold) {
                            return (
                              <strong key={fragIndex}>{fragment.text}</strong>
                            );
                          }
                          return <span key={fragIndex}>{fragment.text}</span>;
                        }
                      );

                      return (
                        <div
                          key={index}
                          className="flex items-start gap-[12px]"
                        >
                          <div className="w-[20px] h-[20px] rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-[2px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M10 3L4.5 8.5L2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <p className="text-[15px] md:text-[16px] text-black leading-relaxed">
                            {content}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            )}
          </div>

          {/* Right Column: Image */}
          {media?.url && (
            <div className="lg:w-1/2 w-full">
              <div className="w-full h-full min-h-[300px] lg:min-h-[400px]">
                <Image
                  src={media.url}
                  alt={media.alternativeText || "MSU Advantages"}
                  width={media.width || 680}
                  height={media.height || 520}
                  className="w-full h-full object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority={false}
                  unoptimized
                />
              </div>
            </div>
          )}
        </div>

        {/* Bottom CTA Section */}
        {studyData.Apply_Now_Content && (
          <div className="mx-[16px] md:mx-[32px] xl:mx-0 bg-[#FFA201] px-[24px] py-[28px] md:px-[40px] md:py-[35px] flex flex-col md:flex-row items-center justify-between gap-[24px] md:gap-[32px]">
            <p className="text-[16px] md:text-[18px] lg:text-[20px] font-bold text-black leading-[1.4] flex-1">
              {studyData.Apply_Now_Content}
            </p>

            <Link
              href="https://application.msu.edu.in/campus-programme-registration"
              className="w-full md:w-auto md:min-w-[188px] flex justify-center items-center gap-3 bg-[#081E2D] text-white px-[34px] py-[19px] hover:bg-[#0a2a3f] transition-all duration-300 group"
            >
              <span className="text-white font-medium">Apply Now</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
              >
                <g clipPath="url(#clip0_3062_15277)">
                  <path
                    d="M12.3592 14.9568L3.31251 5.91015L4.79862 4.42404L13.8453 13.4707L13.8453 5.14852L15.9444 5.1671V17.056H4.05556L4.03698 14.9568L12.3592 14.9568Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_3062_15277">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
